import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Collapse,
  Link,
  Tooltip as MuiTooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useAuth } from '../context/AuthContext';

// ── Types ───────────────────────────────────────────────────────────────────

// Mirror of opt_spy_daily_gex_YYYY-MM-DD.json
interface GexMap {
  underlying: string;
  spot: number;
  computed_at: string;
  expiries: string[];
  net_gex: number;
  regime: 'positive' | 'negative' | 'mixed';
  flip_point: number | null;
  call_wall: number | null;
  put_wall: number | null;
  magnet: number | null;
  // v0.9.1: annualized same-day ATM implied vol (may be absent in map files;
  // the parent can also pass it in via the atmIv prop from the EOD gex block)
  atm_iv?: number | null;
  // v0.11.2: overnight gap data point (absent in older files)
  prev_close?: number | null;
  gap_pct?: number | null;
  gap_vs_sigma?: number | null;
  // v0.16.1: today's economic-calendar event (CPI/NFP/PPI/PCE/FOMC) or null
  event_today?: string | null;
  // v0.17.2: overhead-profile metrics — DATA ONLY, the selector never reads
  // them. stair_up = consecutive positive-GEX strikes directly above spot
  // (0 = upside capped); neg_shelf = first strike below spot with negative
  // GEX; shadow_call_fade_veto = stair_up > 0 (shadow signal under evaluation)
  stair_up?: number | null;
  neg_shelf?: number | null;
  shadow_call_fade_veto?: boolean | null;
  by_strike: { [strike: string]: number };
}

// Consolidated trade + status from /opt-spy-daily/live
interface LiveTrade {
  direction: 'put_credit' | 'call_credit' | null;
  short_strike: number | null;
  long_strike: number | null;
  width: number | null;
  contracts: number | null;
  // v0.27.0: contracts < contracts_requested = partial entry fill
  contracts_requested?: number | null;
  fill_reason?: string | null;
  entry_credit_per_share: number | null;
  entry_credit_total: number | null;
  max_loss_total: number | null;
  exit_level: number | null;
  exit_level_kind: string | null;
  // v0.70.0: "caleb_call" = R21 live template (exit line = the short strike)
  entry_template?: string | null;
  // v0.28.0: non-null = the fallback sold the wall strike itself
  wall_strike_fallback?: { wall?: number; sigma_from_spot?: number } | null;
  // v0.30.0: preference layer (fired:true = sold the wall under preference;
  // would_fire without fired = dark-mode would-have, show muted)
  wall_strike_preference?: {
    wall?: number;
    would_fire?: boolean;
    fired?: boolean;
    wall_credit?: number;
    template_credit?: number;
    note?: string;
  } | null;
  // v0.42/v0.43 (R3/R9): non-null = the trade IS the early wall-strike put
  // (short sold ON the put wall, IV <= 20%, possibly before 10:15). A
  // short_strike_breach exit on these is the wall breaking, not a placement
  // error. Regime comes from regime_at_entry.
  early_wall_put?: { wall?: number; atm_iv?: number; iv_cap?: number; sigma_from_spot?: number; cushion_pct?: number; delta?: number } | null;
  regime_at_entry?: string | null;
  // v0.46.0 (R10, dark): entry credit <= $0.10 — thin-credit cap territory
  thin_credit?: boolean | null;
  // v0.53.0: scan-based session extreme at entry (tag only, no gate); the
  // SESSION_EXTREME bars row (top-level in the response) is preferred
  session_extreme?: { day_low?: number; day_high?: number; margin?: number; traded_through?: boolean; source?: string } | null;
  // v0.56.0: entry grade A/B/C (flags: iv > 16.5% / MIXED regime / spot
  // within 0.30% of the exit line); absent before 10/19 — never recomputed
  entry_grade?: { grade?: string; flags?: string[]; atm_iv?: number; regime?: string; cushion_pct?: number; profit_floor?: boolean } | null;
  // v0.56.1: the AI entry gate's opinion on this trade — fail_open = model
  // unavailable (gate let it through), disabled = gate off; absent on older rows
  ai_gate?: { allow?: boolean; confidence?: number; reason?: string; source?: 'model' | 'fail_open' | 'disabled' } | null;
  // v0.67.1–v0.68.0: ×0.5 size caps, present only when the cap bit
  down_day_call_size?: { gap_pct?: number; factor?: number; from?: number } | null;
  up_day_put_size?: { gap_pct?: number; factor?: number; from?: number } | null;
  event_day_size?: { event?: string; factor?: number; from?: number } | null;
  opened_at: string | null;
  closed_at: string | null;
  exit_reason: string | null;
  close_debit_per_share: number | null;
  realized_pnl_total: number | null;
  pct_of_credit_captured: number | null;
}

interface LiveCheckin {
  mid: number;
  credit: number;
  captured: number;
  upl: number;
  spot: number;
  status: string;
}

interface LiveResp {
  instance: string;
  date: string;
  is_today: boolean;
  state: string | null;
  status: 'open' | 'closed' | 'stand_aside' | 'none';
  trade: LiveTrade | null;
  checkin: LiveCheckin | null;
  stand_aside_reason: string | null;
  // v0.15.x: leader entry attempts that never filled (credit floor held)
  open_failed: { count: number; last_reason: string | null; last_ts: string | null } | null;
  // v0.19.0: morning-session event blocks entries until event time + resume_min
  // (scans/exits keep running) — a deliberate stand-aside, not a malfunction
  event_blackout?: { count: number; reason: string | null; event: string | null; last_ts: string | null } | null;
  // v0.20.0: IV-crush detector lifted the blackout early; the entry is half-sized
  adaptive_resume?: { ts_et: string | null; iv_peak: number | null; iv_now: number | null; iv_drawdown: number | null } | null;
  // v0.31.0: snapshot at the moment of an operator-requested close
  operator_close_request?: { ts_et?: string | null; mid?: number | null; captured_pct?: number | null; unrealized_pnl?: number | null; spot?: number | null } | null;
  // v0.32.0: accounts stood aside — the selector's spread differs from the book's
  book_coherence_veto?: {
    accounts?: string[];
    candidate?: { direction?: string; short_strike?: number; long_strike?: number; credit?: number };
    book?: { direction?: string; short_strike?: number; long_strike?: number; account?: string; status?: string };
  } | null;
  // v0.33.0: broker holds legs the engine doesn't know — red alert, never a trade
  broker_position_unknown?: { ts_et?: string | null; account?: string | null; when?: string | null; reason?: string | null; legs?: { symbol?: string; qty?: number; side?: string }[] | null }[] | null;
  // v0.34.0: cushion-blocked put-morning shadow (grey, never P&L)
  wall_at_spot_shadow?: { short_strike?: number; long_strike?: number; credit?: number; spot?: number; put_wall?: number; cushion_pct?: number; alt_line?: number } | null;
  wall_at_spot_shadow_eod?: { [k: string]: any } | null;
  // v0.36.0: the regime label was untrustworthy (map flipped sign through the
  // open) — the engine refused its spread; SHADOW = veto off, trade is real
  map_conviction_veto?: { warmup_flips?: number; net_gex_m?: number; weak_net?: boolean; flip_veto?: boolean; reason?: string; direction?: string; short_strike?: number; long_strike?: number; credit?: number } | null;
  map_conviction_shadow?: { warmup_flips?: number; [k: string]: any } | null;
  // v0.36.0: magnet-on-put-wall setup record (may coexist with the real trade)
  magnet_at_wall_shadow?: { spot?: number; put_wall?: number; magnet?: number; wall_gap_pct?: number; would_veto?: boolean; put?: { short_strike?: number; long_strike?: number; credit?: number }; call_alt?: { [k: string]: any } | null } | null;
  magnet_at_wall_shadow_eod?: { [k: string]: any } | null;
  // v0.41.0: OPG wall−2 put template on negative mornings (data only)
  below_wall_shadow?: { short_strike?: number; long_strike?: number; credit?: number; put_wall?: number; iv_bucket?: string; atm_iv?: number; ts_et?: string; first_scan?: string } | null;
  below_wall_shadow_eod?: { [k: string]: any } | null;
  // v0.40.0: exit-rule alternatives replay for a stopped trade (grey table)
  exit_shadow_eod?: { exit_reason?: string; actual_pnl?: number; contracts?: number; alternatives?: { [name: string]: { exit?: string; at?: string; pnl_total?: number; delta_vs_actual?: number } } } | null;
  // v0.44.0 Study G: giveback shadow (grey "G" chip, never P&L)
  giveback_shadow?: { fired_at?: string; would_exit_debit?: number; peak_captured?: number; [k: string]: any } | null;
  giveback_shadow_eod?: { fired?: boolean; fired_at?: string; would_exit_debit?: number; peak_captured?: number; shadow_pnl_per_ct?: number; [k: string]: any } | null;
  // v0.45.0 Study H: AI exit desk — verdict trail (compact) + EOD score
  ai_exit_shadow_queries?: { ts?: string; verdict?: string | null; confidence?: number; conf_threshold?: number; reason?: string; error?: string; breach_prob?: number | null; escalated?: boolean | null; interval_min?: number | null; misread?: string | null }[];
  ai_exit_shadow_eod?: { [k: string]: any } | null;
  // v0.43.1: broker options BP set the contract clip (per account)
  sizing_bp_capped?: { ts_et?: string; account?: string; contracts?: number; note?: string }[];
  // v0.53.0: leader fill's SESSION_EXTREME bars row — preferred over the
  // scan-based trade.session_extreme
  session_extreme?: { day_low?: number; day_high?: number; margin?: number; traded_through?: boolean; source?: string } | null;
  // v0.54.0 LIVE decision rows (Trade log, not shadow chips).
  // giveback_exit was LIVE v0.54.0–v0.64.0 only (kept for history);
  // since 10/21 the −50% variant is a shadow again → giveback_live_shadow
  giveback_exit?: { account?: string; mid?: number; credit?: number; captured_pct?: number; peak_captured?: number; arm_pct?: number; exit_pct?: number; spot?: number } | null;
  // v0.64.0 G-live: where the demoted −50% giveback WOULD have fired
  giveback_live_shadow?: { fired_at?: string; ts_et?: string; would_exit_debit?: number; peak_captured?: number; captured_pct?: number; [k: string]: any } | null;
  // v0.56.0 LIVE: grade B/C profit floor fired (35% captured → 30% floor)
  profit_floor_exit?: { account?: string; entry_grade?: string; mid?: number; credit?: number; captured_pct?: number; peak_captured?: number; arm_pct?: number; floor_pct?: number; spot?: number } | null;
  // v0.69.0 LIVE floor AI: watch start, leader votes, fallback, AI exit, close scoreboard row
  caleb_call_live_entry?: { ts_et?: string; dist_pct?: number; dist_actual_pct?: number; size_factor?: number; floor?: number; joined_book?: boolean; [k: string]: any } | null;
  caleb_call_live_skip?: { ts_et?: string; reason?: string; spot?: number } | null;
  floor_ai_watch?: { account?: string; ts_et?: string; mid?: number; captured_pct?: number; peak_captured?: number; exit_conf?: number; confirm?: number; [k: string]: any } | null;
  floor_ai_votes?: { ts?: string; ts_et?: string; mid?: number; verdict?: string; confidence?: number; breach_prob?: number; misread?: any; error?: any; reason?: string }[];
  floor_ai_fallback?: { account?: string; reason?: string; mid?: number; captured_pct?: number; ts_et?: string } | null;
  floor_ai_exit?: { account?: string; exit_at?: string; exit_conf?: number; exit_mid?: number; exit_bp?: number; exit_reason?: string; exit_votes?: number; mid?: number } | null;
  floor_ai_close?: { exit_reason?: string; close_debit?: number; pnl_per_ct?: number; fixed_floor_pnl_per_ct_approx?: number; vs_fixed_floor_per_ct?: number; fallback?: boolean; n_queries?: number; n_voted?: number; n_exit?: number; n_hold?: number; n_errors?: number; n_misreads?: number } | null;
  ai_exit_live?: { account?: string; mid?: number; credit?: number; captured_pct?: number; spot?: number; live_exit_at?: string; live_exit_conf?: number; live_exit_mid?: number; live_exit_reason?: string; live_exit_votes?: number; live_exit_first_vote_at?: string } | null;
  wall_stability_block?: { reason?: string; candidate?: number; direction?: string; raw_exit_level?: number; view?: { [k: string]: any } } | null;
  // v0.35.0: profit-close execution note (grey, never P&L)
  profit_close_concession?: { resting_limit?: number; fill_debit?: number; concession_per_share?: number; concession_usd?: number; contracts?: number } | null;
  profit_close_concession_eod?: { minutes_to_resting?: number | null; [k: string]: any } | null;
  // v0.21.0: SHADOW-phase conviction-ladder row (raw audit row; field names may
  // evolve so it's parsed defensively). Logged only — never affected sizing.
  conviction_shadow?: { [k: string]: any } | null;
}

// v0.19.0: compact research-only QQQ shadow read (never traded, never charted)
interface ShadowRead {
  underlying: string;
  regime: string | null;
  atm_iv: number | null;
  spot: number | null;
  computed_at: string | null;
}

interface GexMapViewProps {
  instanceName: string;
  // Optional day to render (YYYY-MM-DD). Defaults to today ET. Live polling
  // only runs for today; past dates render a static snapshot.
  date?: string;
  // Optional 0DTE ATM implied vol from the EOD file's gex block (v0.9.1) —
  // used when the map file itself doesn't carry atm_iv.
  atmIv?: number | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtNetGex = (v: number): string => {
  const sign = v < 0 ? '-' : '+';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(0)}M`;
  return `${sign}${abs.toFixed(0)}`;
};

const fmtStrike = (v: number): string =>
  Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');

const fmtSignedMoney0 = (v: number): string =>
  `${v >= 0 ? '+' : '-'}$${Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const fmtTimeEt = (iso: string): string => {
  const m = iso.match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : iso;
};

const regimeChip = (regime: GexMap['regime']) => {
  const map: { [key: string]: { label: string; color: 'success' | 'error' | 'default' } } = {
    positive: { label: 'POSITIVE', color: 'success' },
    negative: { label: 'NEGATIVE', color: 'error' },
    mixed: { label: 'MIXED', color: 'default' },
  };
  const cfg = map[regime] || { label: String(regime).toUpperCase(), color: 'default' as const };
  return <Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 'bold' }} />;
};

const regimeSubtitle = (regime: GexMap['regime'], hasTrade: boolean, direction?: string | null): string => {
  switch (regime) {
    case 'positive':
      return 'dealers dampen moves; good day to sell premium';
    case 'mixed':
      return 'unclear tape; half size';
    case 'negative':
      // v0.10.0 low-IV put-wall play / v0.16.0 call-wall fade admit trades on NEGATIVE days
      if (hasTrade) {
        return direction === 'call_credit'
          ? 'dealers amplify moves; fading the call wall as a ceiling, half size'
          : 'dealers amplify moves, but IV is dead-calm; half-sized under the put wall';
      }
      return 'dealers amplify moves; trades only the half-sized wall plays (put wall if IV is calm, call-wall fade otherwise), else stands aside';
    default:
      return '';
  }
};

// v0.9.1 0DTE ATM implied-vol badge: calm ≤ ~15%, elevated 15–25%, high > 25%
const ivChip = (atmIv: number | null | undefined) => {
  if (atmIv == null) return null;
  const pct = atmIv * 100;
  const color: 'success' | 'warning' | 'error' = pct <= 15 ? 'success' : pct <= 25 ? 'warning' : 'error';
  return (
    <Chip
      label={`0DTE IV ${pct.toFixed(1)}%`}
      color={color}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 'bold' }}
    />
  );
};

// v0.11.2 overnight-gap badge: gap vs the implied 1-day move (|σ| ≥ 1 = the gap
// alone already used a full expected day of movement). Data point only, no gate.
const gapChip = (gapPct: number | null | undefined, gapVsSigma: number | null | undefined) => {
  if (gapPct == null) return null;
  const pct = gapPct * 100;
  const sigma = gapVsSigma != null ? ` (${gapVsSigma >= 0 ? '+' : ''}${gapVsSigma.toFixed(1)}σ)` : '';
  return (
    <Chip
      label={`gap ${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%${sigma}`}
      color={gapVsSigma != null && Math.abs(gapVsSigma) >= 1 ? 'warning' : 'default'}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 'bold' }}
    />
  );
};

// v0.16.1: economic-calendar badge ("CPI day"). Data only; FOMC days force-flat by 13:30 ET.
const shortEventName = (e: string): string => {
  const s = e.toLowerCase();
  if (s.includes('fomc')) return 'FOMC';
  if (s.includes('consumer price')) return 'CPI';
  if (s.includes('nonfarm') || s.includes('non-farm')) return 'NFP';
  if (s.includes('producer price')) return 'PPI';
  if (s.includes('pce') || s.includes('personal consumption')) return 'PCE';
  return e;
};

const eventChip = (event?: string | null) => {
  if (!event) return null;
  const short = shortEventName(event);
  const isFomc = short === 'FOMC';
  return (
    <MuiTooltip title={isFomc ? `${event} — everything is force-closed by 13:30 ET` : event} arrow>
      <Chip
        label={`${short} day`}
        size="small"
        variant="outlined"
        color={isFomc ? 'warning' : 'default'}
        sx={{ fontWeight: 'bold' }}
      />
    </MuiTooltip>
  );
};

// v0.17.2: overhead-profile chip (data only — the selector never reads these)
const stairChip = (stairUp?: number | null, negShelf?: number | null) => {
  if (stairUp == null) return null;
  const shelf = negShelf != null ? ` First negative-GEX shelf below spot: ${negShelf} (where downside acceleration starts).` : '';
  const title =
    stairUp > 0
      ? `Overhead profile (data-only research metric): ${stairUp} consecutive positive-GEX strike${stairUp === 1 ? '' : 's'} directly above spot — a "staircase" dealers can walk price up.${shelf}`
      : `Overhead profile (data-only research metric): no positive-GEX strikes directly above spot — upside capped.${shelf}`;
  return (
    <MuiTooltip title={title} arrow>
      <Chip label={`staircase ${stairUp}↑`} size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />
    </MuiTooltip>
  );
};

// v0.21.0/v0.22.0: one-line summary of the conviction-ladder row. Parsed
// defensively since the exact field names may evolve; returns null when the
// tier can't be found. Two modes on the row:
//   applied:true (v0.22.0 live) — the tier ACTUALLY sized the trade. Rungs by
//     era: v0.22.x–v0.28.x {0.50, 0.75, 1.00}; from v0.29.0 (9/15) the rungs
//     are {0.60, 0.80, 1.00, 1.25} — base 0.60 negative/mixed, 1.00 positive,
//     1.25 on a positive-regime morning with >=80% checks (above the sleeve).
//     Narrate plainly, e.g. "conviction 0.80 — sized up from the default 0.60
//     (4/5 checks passed)". (v0.28.2: the iv check no longer fails for
//     sitting near the 16.5% admission cap.)
//   applied absent/false (shadow) — logged only; must never imply it changed
//     the actual position size.
const convictionLine = (
  cs: { [k: string]: any } | null | undefined
): { text: string; applied: boolean } | null => {
  if (!cs) return null;
  const tier = cs.tier ?? cs.conviction_tier ?? cs.would_size_factor ?? cs.size_factor;
  if (tier == null || isNaN(Number(tier))) return null;
  let passed: number | null = cs.checks_passed ?? cs.passed ?? null;
  let known: number | null = cs.checks_known ?? cs.known ?? null;
  const checks = cs.checks;
  if ((passed == null || known == null) && checks && typeof checks === 'object') {
    const vals = (Array.isArray(checks) ? checks : Object.values(checks)).map((v: any) =>
      v !== null && typeof v === 'object' ? (v.pass ?? v.passed ?? v.result) : v
    );
    const isPass = (v: any) => v === true || String(v).toLowerCase() === 'pass';
    const isFail = (v: any) => v === false || String(v).toLowerCase() === 'fail';
    const knownVals = vals.filter((v: any) => isPass(v) || isFail(v));
    known = knownVals.length;
    passed = knownVals.filter(isPass).length;
  }
  const checksPart = passed != null && known ? ` (${passed}/${known} checks passed)` : '';
  if (cs.applied === true) {
    const factor = cs.applied_factor ?? tier;
    const base = cs.base_factor ?? cs.regime_default ?? cs.base ?? null;
    let sizing = '';
    if (base != null && !isNaN(Number(base)) && Number(base) !== Number(factor)) {
      sizing = Number(factor) > Number(base)
        ? `, sized up from the default ${Number(base).toFixed(2)}`
        : `, trimmed from the default ${Number(base).toFixed(2)}`;
    }
    // v0.29.0: a 1.25 rung sizes ABOVE the sleeve (positive regime, >=80% checks)
    const aboveSleeve = Number(factor) > 1.0 ? ', above the full sleeve' : '';
    return { text: `conviction ${Number(factor).toFixed(2)} — sized the trade${sizing}${aboveSleeve}${checksPart}`, applied: true };
  }
  return {
    text: `conviction (shadow): ${Number(tier).toFixed(2)}${checksPart} · logged only — did not affect position size`,
    applied: false,
  };
};

// v0.19.0: research-only shadow map badge (the strategy never trades QQQ;
// its levels are never drawn on the SPY chart — a small note is enough)
const shadowChip = (s: ShadowRead | null) => {
  if (!s || !s.regime) return null;
  const iv = s.atm_iv != null ? ` · IV ${(s.atm_iv * 100).toFixed(1)}%` : '';
  return (
    <MuiTooltip
      title={`Research-only shadow gamma map — the strategy never trades ${s.underlying}. ${s.underlying} regime ${s.regime.toUpperCase()}${s.spot != null ? `, spot ${s.spot}` : ''}${iv}. Not drawn on the SPY chart.`}
      arrow
    >
      <Chip label={`also watching ${s.underlying} (research): ${s.regime.toUpperCase()}${iv}`} size="small" variant="outlined" sx={{ fontWeight: 'bold', opacity: 0.7 }} />
    </MuiTooltip>
  );
};

const prettyExitReason = (reason: string | null): string => {
  const map: { [key: string]: string } = {
    profit_close: 'took profit',
    expired_worthless: 'expired worthless',
    max_loss_close: 'stopped out',
    short_strike_breach: 'short strike breached',
    flip_breach: 'exited early — thesis broke',
    wall_breach: 'exited early — thesis broke',
    forced_eod_close: 'forced flat before the close',
    event_close: 'forced flat before the close',
    // v0.31.0: the operator asked for an immediate exit (close-now flag file)
    operator_close: 'closed on operator request',
    // v0.54.0 LIVE exits: giveback (armed at 33% captured, exits at −50%
    // from peak) and the leader's >= 0.90 AI EXIT verdict
    giveback_close: 'giveback exit — kept most of the win',
    ai_exit: 'AI exit — the desk said go',
    // v0.56.0 LIVE: grade B/C trade reached 35% captured, exited when it
    // fell back to the 30% floor
    profit_floor_close: 'profit floor — locked in ≥ 30% of the credit',
    // v0.69.0 LIVE: after the B/C floor fired the AI watched and said EXIT
    floor_ai_exit: 'floor AI — after the floor fired, the AI chose the exit',
  };
  if (!reason) return 'closed';
  return map[reason] || reason.replace(/_/g, ' ');
};

// v0.28.0: `|at_wall_…` reject suffixes — the wall strike itself was also
// tried as a fallback and refused. Variants: at_wall_770_sigma_0.49<0.60,
// at_wall_770_inside_min_otm, at_wall_credit_too_thin_…, at_wall_770_no_quote.
const prettyAtWallSuffix = (s: string): string => {
  const strike = s.match(/^at_wall_(\d+(?:\.\d+)?)/)?.[1];
  const at = strike ? `the wall strike (${strike})` : 'the wall strike';
  if (s.includes('sigma')) {
    const m = s.match(/sigma_([\d.]+)<([\d.]+)/);
    return `${at} was also tried but sits too close to spot${m ? ` (${m[1]}σ < ${m[2]}σ)` : ''}`;
  }
  if (s.includes('inside_min_otm')) return `${at} was also tried but is inside the minimum OTM distance`;
  if (s.includes('credit_too_thin')) return `${at} was also tried — it didn't pay enough either`;
  if (s.includes('no_quote')) return `${at} was also tried but had no usable quote`;
  return `${at} was also tried as a fallback and refused`;
};

const prettyStandAside = (reason: string | null): string => {
  if (!reason) return 'the system stood aside';
  if (reason.startsWith('event_veto:')) return `event risk — ${reason.slice('event_veto:'.length)}`;
  // v0.19.0: entries deliberately blocked through a morning-session event
  if (reason.startsWith('event_blackout_until_')) {
    const hhmm = reason.slice('event_blackout_until_'.length);
    return `deliberately standing aside through the scheduled event — scanning continues, entries resume ${hhmm} ET`;
  }
  if (reason.startsWith('credit_too_thin')) {
    // v0.27.0 appends the priced strikes (credit_too_thin_0.06<0.08_771/773);
    // v0.28.0 may append |at_wall_… = the wall strike itself was ALSO tried
    // as a fallback and refused for that reason
    const [main, atWall] = reason.split('|');
    const strikes = main.match(/_(\d+(?:\.\d+)?\/\d+(?:\.\d+)?)$/);
    let text = `the spread${strikes ? ` (${strikes[1]})` : ''} didn't pay enough`;
    if (atWall) text += `; ${prettyAtWallSuffix(atWall)}`;
    return text;
  }
  // v0.25.1: the protective long leg had no bid at all — a no-premium morning
  // (older logs say no_long_leg_at_width)
  if (reason.startsWith('long_leg_no_quote') || reason === 'no_long_leg_at_width') {
    return 'the protective leg two strikes out had no bid at all — the market was pricing that side at zero, so there was no spread to sell';
  }
  // v0.54.0 LIVE wall-stability entry gate (pairs with the
  // WALL_STABILITY_BLOCK row rendered under the stand-aside panel)
  if (reason.startsWith('wall_unstable_mode5')) {
    const m = reason.match(/wall_unstable_mode5_(put|call)_([\d.]+)_vs_raw_([\d.]+)/);
    return m
      ? `the ${m[1]} wall is flickering — the stabilized read (mode of the last 5 scans: ${m[2]}) wouldn't clear the cushion floor, even though the raw read (${m[3]}) would; wall-anchored entry skipped`
      : 'the wall is flickering — the stabilized read wouldn\'t clear the cushion floor; wall-anchored entry skipped';
  }
  if (reason.startsWith('long_leg_quote_too_wide')) {
    return 'the protective leg was quoted, but too wide to trust';
  }
  // v0.68.0 down-day call gate: SPY ≥ 1% down vs the prior close blocks
  // every call credit spread (rallies after selloffs run through them)
  if (reason.includes('down_day_call_block')) {
    const m = reason.match(/down_day_call_block_([+-]?[\d.]+)/);
    return `SPY ${m ? `${parseFloat(m[1]) > 0 ? '−' : ''}${m[1].replace('-', '−')}%` : '≥ 1% down'} on the day — no call spreads after a selloff (the down-day call gate)`;
  }
  // v0.36.0: MAP_CONVICTION_VETO days — the regime label itself wasn't
  // trusted. Reason carries the evidence (map_undecided_flips=N_net=±X.XM):
  // flips > 0 = the sign flipped through the open; flips = 0 = the net was
  // simply too weak to call a regime.
  if (reason.startsWith('map_undecided')) {
    const m = reason.match(/flips=(\d+)_net=([+-][\d.]+M)/);
    if (m && m[1] === '0') return `the dealer map was too weak to trust (net ${m[2]}) — no regime call, no trade`;
    if (m) return `the dealer map flipped sign ${m[1]}× through the open (net ${m[2]}) — no trustworthy regime, no trade`;
    return 'the dealer map was too weak or unstable to trust — no regime call, no trade';
  }
  if (reason.startsWith('negative_regime_iv_too_high')) {
    return 'dealers short gamma AND options pricing real movement — the low-IV exception did not apply (and no call wall was there to fade, or the call side did not price)';
  }
  if (reason.startsWith('atm_iv_veto')) {
    return 'options are pricing a panic-sized move (IV above the veto cap) — no structure is trusted on days like this, in any regime';
  }
  // v0.17.1: cushion rejects show the applied bar (0.1% put / 0.3% call) and
  // may be `call:` prefixed — a call-side reject followed by a put-side entry
  // on the same scan is the selector rerouting to the safer side, not an error
  const isCallCushion = reason.startsWith('call:');
  const cushionReason = isCallCushion ? reason.slice('call:'.length) : reason;
  if (cushionReason.startsWith('exit_cushion_too_thin')) {
    const m = cushionReason.match(/([\d.]+)%<([\d.]+)%/);
    const nums = m ? ` (${m[1]}% cushion vs the ${m[2]}%${isCallCushion ? ' call-side' : ''} bar)` : '';
    return `the trade's own exit line was too close to the current price${nums} — normal noise would stop us out immediately${isCallCushion ? '; the system then rechecks the put side' : ''}`;
  }
  const map: { [key: string]: string } = {
    negative_regime_stand_aside: 'dealers are short gamma; selling premium is dangerous, so the system sat out',
    mixed_regime_stand_aside: 'unclear tape; the system stood aside',
    chase_floor_reached: "the market wouldn't fill us at a price worth taking — the credit floor held",
  };
  if (reason.startsWith('unfilled_after')) {
    return "the market wouldn't fill us at a price worth taking";
  }
  return map[reason] || reason.replace(/_/g, ' ');
};

// Current wall-clock in ET, for freshness messaging and the refresh windows
const etNow = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value || '';
  const weekday = get('weekday');
  const hour = parseInt(get('hour'), 10) % 24;
  const minute = parseInt(get('minute'), 10);
  return {
    isWeekend: weekday === 'Sat' || weekday === 'Sun',
    minutes: hour * 60 + minute,
    hhmm: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
  };
};

const todayEt = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

// Level line styling: spot most prominent; walls/flip dashed; magnet dotted.
const LEVEL_STYLES: { [key: string]: { name: string; stroke: string; dash?: string; priority: number } } = {
  spot: { name: 'SPOT', stroke: '#ffffff', priority: 0 },
  flip_point: { name: 'FLIP', stroke: '#ab47bc', dash: '6 4', priority: 2 },
  put_wall: { name: 'PUT WALL', stroke: '#f44336', dash: '6 4', priority: 1 },
  call_wall: { name: 'CALL WALL', stroke: '#4caf50', dash: '6 4', priority: 1 },
  magnet: { name: 'MAGNET', stroke: '#ffa726', dash: '2 4', priority: 3 },
};

const headlineTone: { [key: string]: { color: string; label: string } } = {
  open: { color: '#29b6f6', label: 'OPEN' },
  win: { color: '#4caf50', label: 'CLOSED' },
  loss: { color: '#f44336', label: 'CLOSED' },
  aside: { color: 'rgba(255,255,255,0.35)', label: 'NO TRADE' },
};

// ── Component ─────────────────────────────────────────────────────────────────

const GexMapView: React.FC<GexMapViewProps> = ({ instanceName, date, atmIv }) => {
  const { token } = useAuth();
  const effectiveDate = date || todayEt();
  const isToday = effectiveDate === todayEt();

  const [gexMap, setGexMap] = useState<GexMap | null>(null);
  const [shadow, setShadow] = useState<ShadowRead | null>(null);
  const [isSample, setIsSample] = useState<boolean>(false);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState<LiveResp | null>(null);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  const [showHelp, setShowHelp] = useState<boolean>(false);

  const authHeaders = useCallback((): HeadersInit => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchMap = useCallback(async (sample: boolean = false) => {
    setLoading(true);
    setError(null);
    try {
      const qs = sample ? '?date=sample' : `?date=${effectiveDate}`;
      const res = await fetch(`/api/instances/${instanceName}/gex-map${qs}`, { headers: authHeaders() });
      if (res.status === 404) {
        setGexMap(null);
        setNotFound(true);
        setIsSample(false);
        return;
      }
      if (!res.ok) throw new Error(`Failed to load gamma map (${res.status})`);
      const data = await res.json();
      setGexMap(data.map);
      setShadow(data.shadow || null);
      setIsSample(Boolean(data.is_sample));
      setNotFound(false);
      setFetchedAt(new Date());
    } catch (e: any) {
      setError(e.message || 'Failed to load gamma map');
    } finally {
      setLoading(false);
    }
  }, [instanceName, effectiveDate, authHeaders]);

  // Consolidated trade + live status (open/closed/stand-aside + CHECKIN)
  const fetchLive = useCallback(async () => {
    try {
      const res = await fetch(
        `/api/instances/${instanceName}/opt-spy-daily/live?date=${effectiveDate}`,
        { headers: authHeaders() }
      );
      if (!res.ok) return;
      setLive(await res.json());
    } catch {
      // overlay is best-effort; the map renders without it
    }
  }, [instanceName, effectiveDate, authHeaders]);

  useEffect(() => {
    fetchMap();
    fetchLive();
  }, [fetchMap, fetchLive]);

  // v0.15.2: the map is first written ~09:30 ET (data-only warm-up scans every
  // minute until 09:34), then overwritten every ~5 min through the entry window
  // (09:35–11:30) — poll in that window (today only).
  useEffect(() => {
    if (!isToday) return;
    const id = setInterval(() => {
      const { isWeekend, minutes } = etNow();
      if (!isWeekend && minutes >= 9 * 60 + 30 && minutes <= 11 * 60 + 40) {
        fetchMap();
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [isToday, fetchMap]);

  // Trade status (uPL / captured / spot) updates all session — poll live during market hours (today only).
  useEffect(() => {
    if (!isToday) return;
    const id = setInterval(() => {
      const { isWeekend, minutes } = etNow();
      if (!isWeekend && minutes >= 9 * 60 + 30 && minutes <= 16 * 60) {
        fetchLive();
      }
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [isToday, fetchLive]);

  const chart = useMemo(() => {
    if (!gexMap) return null;
    const data = Object.entries(gexMap.by_strike)
      .map(([k, v]) => ({ strike: parseFloat(k), gexM: v / 1e6 }))
      .sort((a, b) => a.strike - b.strike);
    if (data.length === 0) return null;
    const minStrike = data[0].strike;
    const maxStrike = data[data.length - 1].strike;
    const maxAbs = Math.max(...data.map((d) => Math.abs(d.gexM)), 1e-6);

    // Group level lines by strike so co-located levels get one merged label
    // (call_wall + magnet sharing a strike is common).
    const levels: { key: string; value: number }[] = [];
    (['spot', 'flip_point', 'put_wall', 'call_wall', 'magnet'] as const).forEach((key) => {
      const v = gexMap[key];
      if (v != null) levels.push({ key, value: v });
    });
    const grouped = new Map<number, { keys: string[]; value: number }>();
    levels.forEach((l) => {
      const g = grouped.get(l.value);
      if (g) g.keys.push(l.key);
      else grouped.set(l.value, { keys: [l.key], value: l.value });
    });
    const levelLines = Array.from(grouped.values()).map((g) => {
      const sortedKeys = [...g.keys].sort((a, b) => LEVEL_STYLES[a].priority - LEVEL_STYLES[b].priority);
      const style = LEVEL_STYLES[sortedKeys[0]];
      const label = `${sortedKeys.map((k) => LEVEL_STYLES[k].name).join(' + ')} ${g.value % 1 === 0 ? g.value : g.value.toFixed(2)}`;
      return { value: g.value, label, stroke: style.stroke, dash: style.dash, isSpot: sortedKeys[0] === 'spot' };
    });

    return { data, minStrike, maxStrike, maxAbs, levelLines };
  }, [gexMap]);

  // Trade overlay geometry (profit / loss / max-loss bands + auto-exit line).
  const overlay = useMemo(() => {
    if (!chart || !live || !live.trade) return null;
    if (live.status !== 'open' && live.status !== 'closed') return null;
    const t = live.trade;
    if (t.short_strike == null || t.long_strike == null) return null;
    const S = t.short_strike;
    const L = t.long_strike;
    const E = t.exit_level != null ? t.exit_level : S;
    const xMin = chart.minStrike - 1;
    const xMax = chart.maxStrike + 1;
    const isPut = t.direction !== 'call_credit';
    const bands = isPut
      ? [
          { key: 'profit', x1: S, x2: xMax, fill: '#4caf50', opacity: 0.13 },
          { key: 'loss', x1: L, x2: S, fill: '#f44336', opacity: 0.11 },
          { key: 'maxloss', x1: xMin, x2: L, fill: '#b71c1c', opacity: 0.22 },
        ]
      : [
          { key: 'profit', x1: xMin, x2: S, fill: '#4caf50', opacity: 0.13 },
          { key: 'loss', x1: S, x2: L, fill: '#f44336', opacity: 0.11 },
          { key: 'maxloss', x1: L, x2: xMax, fill: '#b71c1c', opacity: 0.22 },
        ];
    return { S, L, E, isPut, bands, combined: Math.abs(E - S) < 1e-9, maxLoss: t.max_loss_total };
  }, [chart, live]);

  const headline = useMemo(() => {
    if (!live) return null;
    const t = live.trade;
    // v0.15.x no-fill day: the system wanted the trade but entry orders never
    // filled (credit floor held) — tell that story, not a generic stand-aside.
    if ((live.status === 'stand_aside' || live.status === 'none') && live.open_failed) {
      const n = live.open_failed.count;
      const gate = live.stand_aside_reason ? ` Latest gate: ${prettyStandAside(live.stand_aside_reason)}.` : '';
      return {
        text: `No fill ${isToday ? 'today' : 'this day'} — the system tried to enter ${n}× but the market wouldn't fill at a price worth taking (credit floor held).${gate}`,
        tone: 'aside' as const,
      };
    }
    if (live.status === 'stand_aside') {
      // v0.19.0: name the event when entries are blocked through a morning
      // speech/release — this is the system deliberately waiting, not stuck
      if (live.event_blackout && live.stand_aside_reason?.startsWith('event_blackout_until_')) {
        const hhmm = live.stand_aside_reason.slice('event_blackout_until_'.length);
        const ev = live.event_blackout.event || 'the scheduled event';
        return {
          text: `Deliberately standing aside through ${ev} — scanning continues; entries resume ${hhmm} ET. A trade later ${isToday ? 'today' : 'this day'} would be the post-event entry working as designed.`,
          tone: 'aside' as const,
        };
      }
      return { text: `No trade ${isToday ? 'today' : 'this day'} — ${prettyStandAside(live.stand_aside_reason)}`, tone: 'aside' as const };
    }
    if ((live.status === 'open' || live.status === 'closed') && t && t.short_strike != null) {
      const S = fmtStrike(t.short_strike);
      const L = t.long_strike != null ? fmtStrike(t.long_strike) : '?';
      const kind = t.direction === 'call_credit' ? 'call' : 'put';
      const side = t.direction === 'call_credit' ? 'below' : 'above';
      const credit = t.entry_credit_per_share != null ? `$${t.entry_credit_per_share.toFixed(2)}` : '?';
      const E = t.exit_level != null ? fmtStrike(t.exit_level) : S;
      const eKind = t.exit_level_kind === 'short_strike' ? ' (the short strike)' : '';
      // v0.27.0: a partial entry is "1 of 30 filled", never a 1-contract trade.
      // v0.59.0: filled_topup = a partial that raced the cancel was kept and
      // the remainder chased to a full fill (credit = quantity-weighted);
      // partial_topup_<why> = the top-up ran out of ladder mid-chase
      const isPartial =
        t.contracts_requested != null && t.contracts != null && t.contracts < t.contracts_requested;
      const qty = isPartial
        ? `${t.contracts} of ${t.contracts_requested} filled (${t.fill_reason?.startsWith('partial_topup')
            ? 'partial entry — a kept partial was topped up but the chase ran out of ladder'
            : 'partial entry — the chase ended before the full size filled'})`
        : t.fill_reason === 'filled_topup'
        ? `${t.contracts ?? '?'} (a partial raced the cancel, was kept, and topped up to the full size — credit is the weighted average)`
        : `${t.contracts ?? '?'}`;
      // v0.28.0/v0.30.0/v0.42.0: the short leg IS the wall strike (fallback,
      // preference, or the early wall-strike put — R3/R9)
      const wallNote = t.entry_template === 'caleb_call'
        ? ' R21 Caleb call — 0.65% over spot at the open, ×0.5 size.'
        : t.early_wall_put
        ? ' Sold the wall strike itself (early wall-strike put, R3/R9).'
        : t.wall_strike_preference?.fired
        ? ' Sold the wall strike (preference).'
        : t.wall_strike_fallback
        ? ' Sold the wall strike (fallback).'
        : '';
      const base = `Sold the SPY ${S}/${L} ${kind} spread for ${credit} × ${qty} — we win if SPY stays ${side} ${S} into the close. Auto-exit if SPY crosses ${E}${eKind}.${wallNote}`;
      if (live.status === 'open') {
        let statusPart = ' Status: OPEN';
        if (live.checkin?.upl != null) statusPart += `, ${fmtSignedMoney0(live.checkin.upl)}`;
        if (live.checkin?.captured != null) statusPart += ` (${live.checkin.captured}% captured)`;
        statusPart += '.';
        return { text: base + statusPart, tone: 'open' as const };
      }
      const pnl = t.realized_pnl_total ?? 0;
      return {
        text: `${base} Status: CLOSED — ${prettyExitReason(t.exit_reason)}, ${fmtSignedMoney0(pnl)}.`,
        tone: pnl >= 0 ? ('win' as const) : ('loss' as const),
      };
    }
    return null;
  }, [live, isToday]);

  const { isWeekend, minutes, hhmm } = etNow();

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            SPY Dealer Gamma Exposure (GEX)
            {gexMap && regimeChip(gexMap.regime)}
            {gexMap && (
              <Chip label={`net ${fmtNetGex(gexMap.net_gex)}`} size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />
            )}
            {ivChip(gexMap?.atm_iv ?? atmIv)}
            {gapChip(gexMap?.gap_pct, gexMap?.gap_vs_sigma)}
            {eventChip(gexMap?.event_today)}
            {stairChip(gexMap?.stair_up, gexMap?.neg_shelf)}
            {shadowChip(shadow)}
            {isSample && <Chip label="SAMPLE DATA" color="warning" size="small" sx={{ fontWeight: 'bold' }} />}
          </Typography>
          {gexMap && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', display: 'block' }}>
              {gexMap.regime.toUpperCase()} — {regimeSubtitle(gexMap.regime, live?.status === 'open' || live?.status === 'closed', live?.trade?.direction)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {gexMap
              ? `Computed ${gexMap.computed_at.slice(0, 10)} ${fmtTimeEt(gexMap.computed_at)} ET · expiries ${gexMap.expiries.join(', ')} · overwritten through the entry window (~11:30 ET)`
              : 'The strategy writes the map on trading days from ~09:30 ET (warm-up scans), refreshing through the entry window (~11:30 ET).'}
            {fetchedAt && ` · fetched ${fetchedAt.toLocaleTimeString()}`}
          </Typography>
        </Box>
        <MuiTooltip title="Refresh map">
          <span>
            <IconButton onClick={() => { fetchMap(); fetchLive(); }} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </span>
        </MuiTooltip>
      </Box>

      {/* v0.33.0: the broker holds option legs the engine's state doesn't know
          (or the positions lookup failed) — entries were blocked; a human must
          reconcile. Red alert, NEVER rendered as a trade. */}
      {live?.broker_position_unknown && live.broker_position_unknown.length > 0 && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          <strong>UNMANAGED POSITION AT BROKER</strong> — entries blocked; reconcile by hand.
          {live.broker_position_unknown.map((b, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block' }}>
              {b.account || 'account?'} ({b.when || '?'})
              {b.reason
                ? ` — ${b.reason.replace(/_/g, ' ')}`
                : b.legs && b.legs.length > 0
                ? ` — ${b.legs.map((l) => `${l.side || ''} ${l.qty ?? '?'}× ${l.symbol || '?'}`.trim()).join(', ')}`
                : ''}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Plain-language headline */}
      {headline && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            borderRadius: 1,
            borderLeft: `4px solid ${headlineTone[headline.tone].color}`,
            bgcolor: 'rgba(255,255,255,0.04)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
            {headline.text}
          </Typography>
          {/* v0.70.0 R21 LIVE: why the open call did not fire (latest skip, one line) */}
          {live?.caleb_call_live_skip?.reason && !live.caleb_call_live_entry && live.trade?.entry_template !== 'caleb_call' && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
              R21 waiting: {live.caleb_call_live_skip.reason.replace(/_/g, ' ')}
              {live.caleb_call_live_skip.ts_et ? ` (${live.caleb_call_live_skip.ts_et.slice(11, 16)})` : ''}
            </Typography>
          )}
          {/* v0.54.0 LIVE wall-stability entry gate: the blocked candidate's
              numbers (the stand-aside reason above carries mode vs raw) */}
          {live?.wall_stability_block && (() => {
            const b = live.wall_stability_block!;
            const v = b.view || {};
            const bits = [
              b.candidate != null ? `blocked candidate ${b.direction === 'call_credit' ? 'call' : 'put'} ${b.candidate}` : null,
              b.raw_exit_level != null ? `raw exit line ${b.raw_exit_level}` : null,
              v.mode_wall != null ? `mode5 wall ${v.mode_wall}` : v.mode != null ? `mode5 wall ${v.mode}` : null,
              v.run_wall != null ? `run3 wall ${v.run_wall}` : null,
            ].filter(Boolean);
            return bits.length ? (
              <Typography variant="caption" sx={{ color: '#ffb74d', display: 'block', mt: 0.5 }}>
                Wall-stability gate (LIVE since v0.54.0): {bits.join(' · ')}. The exit line stays the raw read; the run3 variant keeps recording in the shadows.
              </Typography>
            ) : null;
          })()}
          {/* v0.17.2: shadow signal under evaluation — flag call-spread days
              where the staircase profile would have vetoed the trade */}
          {gexMap?.shadow_call_fade_veto === true &&
            live?.trade?.direction === 'call_credit' &&
            (live.status === 'open' || live.status === 'closed') && (
              <Typography variant="caption" sx={{ color: '#ffb74d', display: 'block', mt: 0.5 }}>
                Note: the shadow staircase signal ({gexMap.stair_up}↑ positive-GEX strikes above spot) would have vetoed this trade — a research signal under evaluation, not yet a live gate.
              </Typography>
            )}
          {/* v0.18.0: mixed mornings with a staircase above spot and the magnet
              at/above the call wall route to the put side first */}
          {live?.trade?.direction === 'put_credit' &&
            (live.status === 'open' || live.status === 'closed') &&
            gexMap != null && (gexMap.stair_up ?? 0) > 0 &&
            gexMap.magnet != null && gexMap.call_wall != null &&
            gexMap.magnet >= gexMap.call_wall && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5 }}>
                The staircase rule routed us to the put side — the tape was being pulled toward the call wall ({gexMap.stair_up}↑ positive-GEX strikes above spot, magnet at/above the wall).
              </Typography>
            )}
          {/* v0.21.0/v0.22.0: conviction ladder — applied tiers narrate plainly
              (they sized the trade); shadow rows stay muted, one line max */}
          {(() => {
            const cl = convictionLine(live?.conviction_shadow);
            if (!cl) return null;
            return (
              <Typography
                variant="caption"
                sx={{ color: cl.applied ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}
              >
                {cl.text}
              </Typography>
            );
          })()}
          {/* v0.42.0/v0.43.0 (R3/R9): the trade is the early wall-strike put —
              short sold ON the put wall; the exit level IS the short strike,
              so a short_strike_breach here is the wall breaking, not a
              placement error */}
          {live?.trade?.early_wall_put && (live.status === 'open' || live.status === 'closed') && (() => {
            const ew = live.trade!.early_wall_put!;
            return (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mt: 0.5 }}>
                Wall-strike put (R3/R9)
                {live.trade!.regime_at_entry ? ` — ${live.trade!.regime_at_entry.toUpperCase()} regime` : ''}
                {ew.atm_iv != null ? `, IV ${(ew.atm_iv * 100).toFixed(1)}%${ew.iv_cap != null ? ` (cap ${(ew.iv_cap * 100).toFixed(0)}%)` : ''}` : ''}
                {ew.cushion_pct != null ? `, cushion ${ew.cushion_pct}%` : ''}.
                The exit level equals the short strike — a short-strike-breach exit on this trade is the wall breaking, not a placement error.
              </Typography>
            );
          })()}
          {/* v0.46.0 (R10, dark): thin-credit entry — the size cap would have
              (or, armed, did) trim the ladder factor */}
          {live?.trade?.thin_credit && (live.status === 'open' || live.status === 'closed') && (() => {
            const cs = live.conviction_shadow || {};
            const armed = cs.thin_credit_capped_from != null;
            return (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5 }}>
                Thin-credit entry (credit ≤ $0.10)
                {armed
                  ? ` — the size cap trimmed the ladder factor to base × ${cs.applied_factor ?? '1.0'} (from ×${cs.thin_credit_capped_from}).`
                  : cs.thin_credit_would_cap_from != null
                  ? ` — the thin-credit cap (R10, dark) would have trimmed the ladder from ×${cs.thin_credit_would_cap_from} to ×${cs.thin_credit_would_cap_to ?? '1.0'}; cap is OFF, actual factor ×${cs.applied_factor ?? '?'}.`
                  : ' — thin-credit cap territory (R10, dark).'}
              </Typography>
            );
          })()}
          {/* v0.43.2: the cushion rung FAILED and the sizing factor was capped
              at base × 0.5 — marks the thin-cushion set that produced both
              structural-exit losses */}
          {live?.conviction_shadow?.cushion_capped_from != null && (
            <Typography variant="caption" sx={{ color: '#ffb74d', display: 'block', mt: 0.5 }}>
              Cushion-capped: the cushion rung failed, so the sizing factor was capped at base × 0.5
              (from {live.conviction_shadow.cushion_capped_from}) — a thin-cushion entry.
            </Typography>
          )}
          {/* v0.67.1–v0.68.0: ×0.5 size caps — only present when the cap bit
              (from = the factor before the cap, factor = the ceiling) */}
          {(live?.status === 'open' || live?.status === 'closed') && (() => {
            const t = live!.trade;
            if (!t) return null;
            const chips: { label: string; tip: string }[] = [];
            const gap = (g?: number) => (g != null ? `SPY ${g >= 0 ? '+' : '−'}${Math.abs(g).toFixed(2)}% on the day` : '');
            if (t.down_day_call_size) chips.push({ label: 'down-day call, half size', tip: `${gap(t.down_day_call_size.gap_pct)} — call spread capped at ×${t.down_day_call_size.factor ?? 0.5} (from ×${t.down_day_call_size.from ?? '?'})` });
            if (t.up_day_put_size) chips.push({ label: 'up-day put, half size', tip: `${gap(t.up_day_put_size.gap_pct)} — put spread capped at ×${t.up_day_put_size.factor ?? 0.5} (from ×${t.up_day_put_size.from ?? '?'})` });
            if (t.event_day_size) chips.push({ label: `${t.event_day_size.event || 'event'} half size`, tip: `traded event day — capped at ×${t.event_day_size.factor ?? 0.5} (from ×${t.event_day_size.from ?? '?'}); still open at 13:30 → event_close` });
            if (!chips.length) return null;
            return (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                {chips.map((c) => (
                  <MuiTooltip key={c.label} title={c.tip}>
                    <Chip label={c.label} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                  </MuiTooltip>
                ))}
              </Box>
            );
          })()}
          {/* v0.43.1: broker options buying power set the clip, not the sleeve
              budget — a run of these on one account means its sleeve exceeds
              what it can margin */}
          {(live?.sizing_bp_capped?.length ?? 0) > 0 && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5 }}>
              BP-capped: {live!.sizing_bp_capped!.map((b) => `${b.account ?? '?'} ${b.note ? b.note.replace(/^\[BP cap: /, '').replace(/\]$/, '') : `clipped to ${b.contracts ?? '?'} ct`}`).join(' · ')}
              {' '}— the broker's options buying power, not the sleeve budget, set these clips.
            </Typography>
          )}
          {/* v0.53.0: session-extreme tag — how close the day's low (puts) /
              high (calls) came to the short strike; <= 0 = traded through.
              Prefer the bars row over the scan-based version. Tag only. */}
          {(live?.status === 'open' || live?.status === 'closed') && (() => {
            const se = live?.session_extreme || live?.trade?.session_extreme;
            if (!se || se.margin == null) return null;
            const through = se.traded_through || se.margin <= 0;
            return (
              <Typography variant="caption" sx={{ color: through ? '#ef5350' : 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5 }}>
                Session extreme: {se.day_low != null ? `day low ${se.day_low}` : se.day_high != null ? `day high ${se.day_high}` : '?'} —
                {' '}{Number(se.margin).toFixed(2)} pts from the short strike{se.source ? ` (${se.source})` : ''}
                {through ? ' — the strike traded through.' : '.'}
              </Typography>
            );
          })()}
          {/* v0.56.0: entry grade chip — A green / B amber / C red with the
              flags spelled out; pre-10/19 trades have no grade (no chip) */}
          {live?.trade?.entry_grade?.grade && (live.status === 'open' || live.status === 'closed') && (() => {
            const g = live.trade!.entry_grade!;
            const color = g.grade === 'A' ? '#66bb6a' : g.grade === 'B' ? '#ffb74d' : '#ef5350';
            const flagText = (g.flags || []).map((f) =>
              f === 'iv' ? `IV ${g.atm_iv != null ? (g.atm_iv * 100).toFixed(1) + '%' : '> 16.5%'}`
              : f === 'mixed' ? 'MIXED regime'
              : f === 'cushion' ? `entry ${g.cushion_pct != null ? (g.cushion_pct * 100).toFixed(2) + '%' : '< 0.30%'} from the exit line`
              : f).join(' · ');
            // v0.56.1: the AI entry gate's opinion, beside the grade
            const gate = live.trade!.ai_gate;
            const gateChip = gate ? (
              <MuiTooltip title={gate.reason || ''}>
                <Chip
                  label={gate.source === 'fail_open' ? 'AI unavailable'
                    : gate.source === 'disabled' ? 'AI off'
                    : `AI ${gate.confidence != null ? `${Math.round(gate.confidence * 100)}%` : gate.allow ? 'allow' : 'reject'}`}
                  size="small" variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 18, ml: 0.5 }}
                />
              </MuiTooltip>
            ) : null;
            return (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'rgba(255,255,255,0.7)' }}>
                <Chip label={g.grade} size="small" sx={{ fontWeight: 'bold', fontSize: '0.75rem', height: 18, mr: 0.5, bgcolor: color, color: '#000' }} />
                Entry grade {g.grade}{flagText ? ` — ${flagText}` : ' — clean entry'}
                {g.profit_floor ? ' · profit floor armed at 35% captured' : ''}
                {gateChip}
              </Typography>
            );
          })()}
          {/* v0.56.0 LIVE: the profit floor fired — grade B/C trade reached
              35% captured and fell back to the 30% floor */}
          {live?.profit_floor_exit && (
            <Typography variant="caption" sx={{ color: '#81c784', display: 'block', mt: 0.5 }}>
              Profit floor (LIVE): grade {live.profit_floor_exit.entry_grade ?? 'B/C'} trade reached
              {' '}{live.profit_floor_exit.peak_captured != null ? `${Math.round(live.profit_floor_exit.peak_captured * 100)}%` : '≥ 35%'} captured and fell back to the
              {' '}{live.profit_floor_exit.floor_pct != null ? `${Math.round(live.profit_floor_exit.floor_pct * 100)}%` : '30%'} floor
              — closed {live.profit_floor_exit.account ?? 'leader'} at mid ${Number(live.profit_floor_exit.mid ?? 0).toFixed(2)}.
            </Typography>
          )}
          {/* v0.69.0 LIVE floor AI: the floor fire starts a watch (trade NOT
              closed); the AI is asked every check-in and its first EXIT ≥
              the confidence bar closes every watching account */}
          {live?.floor_ai_watch && (() => {
            const w = live.floor_ai_watch!;
            const votes = live.floor_ai_votes || [];
            const close = live.floor_ai_close;
            const ex = live.floor_ai_exit;
            const fb = live.floor_ai_fallback;
            const pct = (v?: number) => (v != null ? `${Math.round(v * 100)}%` : '?');
            const $ct = (v?: number) => (v != null ? `${v >= 0 ? '+' : '−'}$${Math.abs(v).toFixed(0)}/ct` : '—');
            return (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#81c784', display: 'block' }}>
                  Floor AI (LIVE): the profit floor fired at {w.ts_et ? w.ts_et.slice(11, 16) : '?'} (peak {pct(w.peak_captured)}, now {pct(w.captured_pct)} captured)
                  {' '}— instead of closing, the AI watched{w.exit_conf != null ? ` (exits on EXIT ≥ ${Number(w.exit_conf).toFixed(2)})` : ''}.
                  {ex && <> AI said EXIT{ex.exit_at ? ` at ${ex.exit_at.slice(11, 16)}` : ''}{ex.exit_conf != null ? ` (conf ${Number(ex.exit_conf).toFixed(2)}${ex.exit_bp != null ? `, breach ${Number(ex.exit_bp).toFixed(2)}` : ''})` : ''}{ex.exit_reason ? ` — "${ex.exit_reason}"` : ''}.</>}
                  {fb && <span style={{ color: '#ffb74d' }}> Fell back to the fixed 30% floor ({String(fb.reason || '?').replace(/_/g, ' ')}).</span>}
                </Typography>
                {votes.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 0.25, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>votes:</Typography>
                    {votes.map((v, i) => {
                      const bad = v.error || v.misread;
                      const exit = String(v.verdict).toUpperCase() === 'EXIT';
                      return (
                        <MuiTooltip key={i} title={`${(v.ts_et || v.ts || '').slice(11, 16)} ${bad ? (v.error ? `error: ${v.error}` : 'misread — discarded') : `${v.verdict} ${v.confidence != null ? Number(v.confidence).toFixed(2) : ''}${v.breach_prob != null ? `, breach ${Number(v.breach_prob).toFixed(2)}` : ''}`}${v.mid != null ? ` · mid $${Number(v.mid).toFixed(2)}` : ''}${v.reason ? ` — ${v.reason}` : ''}`}>
                          <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: bad ? '#9e9e9e' : exit ? '#ef5350' : '#66bb6a' }} />
                        </MuiTooltip>
                      );
                    })}
                  </Box>
                )}
                {close && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.75)' }}>
                    Closed {String(close.exit_reason || '').replace(/_/g, ' ')} at ${Number(close.close_debit ?? 0).toFixed(2)} → {$ct(close.pnl_per_ct)}
                    {' '}vs fixed floor ≈ {$ct(close.fixed_floor_pnl_per_ct_approx)}:
                    {' '}<b style={{ color: (close.vs_fixed_floor_per_ct ?? 0) >= 0 ? '#66bb6a' : '#ef5350' }}>{$ct(close.vs_fixed_floor_per_ct)}</b>
                    {close.n_queries != null ? ` · ${close.n_voted ?? 0}/${close.n_queries} votes (${close.n_exit ?? 0} exit, ${close.n_hold ?? 0} hold${close.n_errors ? `, ${close.n_errors} err` : ''}${close.n_misreads ? `, ${close.n_misreads} misread` : ''})` : ''}
                  </Typography>
                )}
              </Box>
            );
          })()}
          {/* v0.64.0 G-live: the demoted −50% giveback (shadow again since
              10/21) — where it WOULD have fired; grey, never a trade */}
          {live?.giveback_live_shadow && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}>
              <Chip label="G-live" size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 16, mr: 0.5 }} />
              −50% giveback shadow (demoted from LIVE 10/21) would have exited
              {live.giveback_live_shadow.fired_at || live.giveback_live_shadow.ts_et ? ` at ${String(live.giveback_live_shadow.fired_at || live.giveback_live_shadow.ts_et).slice(11, 16)}` : ''}
              {live.giveback_live_shadow.would_exit_debit != null ? ` ($${Number(live.giveback_live_shadow.would_exit_debit).toFixed(2)})` : ''}
              {live.giveback_live_shadow.peak_captured != null ? ` — peak ${Math.round(live.giveback_live_shadow.peak_captured * 100)}% captured` : ''}
              {' '}— study row, never in the P&L.
            </Typography>
          )}
          {/* v0.54.0 LIVE (until v0.64.0, 10/21): the giveback exit fired —
              historical days only now (close landed as giveback_close) */}
          {live?.giveback_exit && (
            <Typography variant="caption" sx={{ color: '#81c784', display: 'block', mt: 0.5 }}>
              Giveback exit (LIVE): armed at {live.giveback_exit.arm_pct != null ? `${Math.round(live.giveback_exit.arm_pct * 100)}%` : '33%'} captured,
              fired with {live.giveback_exit.captured_pct != null ? `${Math.round(live.giveback_exit.captured_pct * 100)}%` : '?'} of the credit left
              (peak {live.giveback_exit.peak_captured != null ? `${Math.round(live.giveback_exit.peak_captured * 100)}%` : '?'})
              — mid ${Number(live.giveback_exit.mid ?? 0).toFixed(2)} on ${live.giveback_exit.account ?? 'leader'}.
            </Typography>
          )}
          {/* v0.54.0 LIVE: the leader's >= 0.90 AI EXIT verdict was acted on
              (close lands as ai_exit) */}
          {live?.ai_exit_live && (
            <Typography variant="caption" sx={{ color: '#81c784', display: 'block', mt: 0.5 }}>
              AI exit (LIVE): the desk said EXIT at conf {Number(live.ai_exit_live.live_exit_conf ?? 0).toFixed(2)}
              {live.ai_exit_live.live_exit_at ? ` at ${live.ai_exit_live.live_exit_at.slice(11, 16)}` : ''}
              {live.ai_exit_live.live_exit_votes != null ? ` (${live.ai_exit_live.live_exit_votes} vote${live.ai_exit_live.live_exit_votes === 1 ? '' : 's'}${live.ai_exit_live.live_exit_first_vote_at ? `, first ${live.ai_exit_live.live_exit_first_vote_at.slice(11, 16)}` : ''})` : ''}
              {live.ai_exit_live.live_exit_reason ? ` — "${live.ai_exit_live.live_exit_reason}"` : ''} — closed {live.ai_exit_live.account ?? 'leader'} at mid ${Number(live.ai_exit_live.mid ?? live.ai_exit_live.live_exit_mid ?? 0).toFixed(2)}.
            </Typography>
          )}
          {/* v0.44.0 Study G: the giveback rule would have scratched this trade
              — grey chip, data only */}
          {live?.giveback_shadow_eod?.fired === true && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}>
              <Chip label="G" size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 16, mr: 0.5 }} />
              Giveback shadow: would have scratched at
              {live.giveback_shadow_eod.would_exit_debit != null ? ` $${live.giveback_shadow_eod.would_exit_debit.toFixed(2)}` : ''}
              {live.giveback_shadow_eod.fired_at ? ` ${String(live.giveback_shadow_eod.fired_at).slice(11, 16)}` : ''}
              {live.giveback_shadow_eod.peak_captured != null ? `, peak ${(live.giveback_shadow_eod.peak_captured * 100).toFixed(0)}% captured` : ''}
              {' '}— study row, never in the P&L.
            </Typography>
          )}
          {live?.giveback_shadow && !live?.giveback_shadow_eod && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}>
              <Chip label="G" size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 16, mr: 0.5 }} />
              Giveback shadow fired
              {live.giveback_shadow.fired_at ? ` at ${String(live.giveback_shadow.fired_at).slice(11, 16)}` : ''}
              {live.giveback_shadow.peak_captured != null ? ` (peak ${(live.giveback_shadow.peak_captured * 100).toFixed(0)}% captured)` : ''}
              {' '}— scores at EOD; study row, never in the P&L.
            </Typography>
          )}
          {/* v0.45.0 Study H: AI exit desk verdict trail — thin dot strip
              (grey hold / amber low-conf exit / red confident exit / hollow
              error), reason on hover; data only */}
          {(live?.ai_exit_shadow_queries?.length ?? 0) > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', mr: 0.5 }}>
                AI exit desk ({live!.ai_exit_shadow_queries!.length} queries — study, never a trade):
              </Typography>
              {live!.ai_exit_shadow_queries!.map((q, i) => {
                const thr = q.conf_threshold ?? 0.7;
                // v0.60.0 (prompt v5): misread vote discarded & re-asked — violet
                const color = q.error ? 'transparent'
                  : q.misread ? '#ba68c8'
                  : q.verdict === 'exit' ? ((q.confidence ?? 0) >= thr ? '#ef5350' : '#ffb74d')
                  : 'rgba(255,255,255,0.4)';
                return (
                  <MuiTooltip key={i} title={`${(q.ts || '').slice(11, 16)} ${q.error ? `error: ${q.error}` : `${q.verdict ?? '—'}${q.confidence != null ? ` (${q.confidence.toFixed(2)})` : ''}${q.misread ? ` · MISREAD (${q.misread.replace(/_/g, ' ')}) — vote discarded, re-asked` : ''}${q.breach_prob != null ? ` · p(breach) ${q.breach_prob.toFixed(2)}` : ''}${q.escalated ? ` · escalated to ${q.interval_min ?? 1}-min queries` : ''}${q.reason ? ` — ${q.reason}` : ''}`}`}>
                    <span style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%', margin: '0 1.5px',
                      backgroundColor: color, border: q.error ? '1.5px solid rgba(255,255,255,0.4)' : 'none', verticalAlign: 'middle',
                    }} />
                  </MuiTooltip>
                );
              })}
            </Box>
          )}
          {/* v0.31.0: operator-requested close — show the moment-of-request
              snapshot next to the exit so the fill can be judged against it */}
          {live?.trade?.exit_reason === 'operator_close' && live.operator_close_request && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5 }}>
              Closed on operator request
              {live.operator_close_request.ts_et ? ` at ${live.operator_close_request.ts_et}` : ''}
              {live.operator_close_request.mid != null ? ` — mid $${live.operator_close_request.mid.toFixed(2)}` : ''}
              {live.operator_close_request.captured_pct != null ? `, ${(live.operator_close_request.captured_pct * 100).toFixed(0)}% captured` : ''}
              {live.operator_close_request.unrealized_pnl != null ? `, uP&L ${fmtSignedMoney0(live.operator_close_request.unrealized_pnl)}` : ''}
              {live.operator_close_request.spot != null ? `, spot ${live.operator_close_request.spot}` : ''} at the moment of the request.
            </Typography>
          )}
          {/* v0.35.0: execution-quality note under a profit close — the $ the
              close ladder paid over the parked target (informational, not P&L) */}
          {live?.trade?.exit_reason === 'profit_close' && live.profit_close_concession && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}>
              Execution: {(live.profit_close_concession.concession_usd ?? 0) === 0
                ? 'the parked profit target was achieved — no concession paid.'
                : `paid $${live.profit_close_concession.fill_debit?.toFixed(2) ?? '?'} vs $${live.profit_close_concession.resting_limit?.toFixed(2) ?? '?'} resting — $${Math.abs(live.profit_close_concession.concession_usd ?? 0).toFixed(0)} concession${
                    live.profit_close_concession_eod?.minutes_to_resting != null
                      ? `; the target printed ${live.profit_close_concession_eod.minutes_to_resting} min later`
                      : ''
                  }.`}
            </Typography>
          )}
          {/* v0.32.0: book-coherence veto — accounts stood aside because the
              selector's spread differs from the one the book already holds */}
          {live?.book_coherence_veto && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5 }}>
              {(live.book_coherence_veto.accounts || []).join(', ') || 'Some accounts'} stood aside — book already in{' '}
              {live.book_coherence_veto.book
                ? `the ${live.book_coherence_veto.book.short_strike ?? '?'}/${live.book_coherence_veto.book.long_strike ?? '?'} ${live.book_coherence_veto.book.direction === 'call_credit' ? 'call' : 'put'} spread`
                : 'a different spread'}{' '}
              (selector wanted {live.book_coherence_veto.candidate ? `${live.book_coherence_veto.candidate.short_strike ?? '?'}/${live.book_coherence_veto.candidate.long_strike ?? '?'}` : 'another'}).
            </Typography>
          )}
          {/* v0.36.0: map-conviction veto — the engine had a spread but the
              regime read was untrustworthy; show the flips count + the greyed
              would-be spread */}
          {live?.map_conviction_veto && (() => {
            const v = live.map_conviction_veto!;
            const evidence =
              (v.warmup_flips ?? 0) > 0
                ? `the map flipped sign ${v.warmup_flips}× through the open`
                : `the net was too weak to trust${v.net_gex_m != null ? ` (${v.net_gex_m >= 0 ? '+' : ''}${v.net_gex_m}M)` : ''}`;
            const traded = live.status === 'open' || live.status === 'closed';
            return (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}>
                {traded
                  // veto on an early scan, then the map firmed and the engine traded
                  ? `An early scan was vetoed — ${evidence}; the map firmed on a later scan and today's trade is real.`
                  : `Map conviction veto: ${evidence} — no regime call, no trade.${
                      v.short_strike != null
                        ? ` Would-be spread: ${v.short_strike}/${v.long_strike ?? '?'} ${v.direction === 'call_credit' ? 'call' : 'put'}${v.credit != null ? ` ($${v.credit.toFixed(2)} credit)` : ''} — not taken.`
                        : ''
                    }`}
              </Typography>
            );
          })()}
          {/* v0.36.0: same read with the veto flag OFF — informational only;
              the trade that follows is real */}
          {live?.map_conviction_shadow && !live?.map_conviction_veto && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}>
              Map-conviction note (shadow): the map flipped sign
              {live.map_conviction_shadow.warmup_flips != null ? ` ${live.map_conviction_shadow.warmup_flips}×` : ''} through the open — logged only, the veto is off and today's trade is real.
            </Typography>
          )}
          {/* v0.36.0: magnet sat on the put wall within 0.6% of spot — setup
              record (the put may ALSO be the real trade); grey, never P&L */}
          {live?.magnet_at_wall_shadow && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}>
              Shadow setup: the magnet sat on the put wall
              {live.magnet_at_wall_shadow.wall_gap_pct != null ? ` (${live.magnet_at_wall_shadow.wall_gap_pct}% from spot)` : ''}
              {live.magnet_at_wall_shadow.put?.short_strike != null
                ? ` — recorded put ${live.magnet_at_wall_shadow.put.short_strike}/${live.magnet_at_wall_shadow.put.long_strike ?? '?'}${live.magnet_at_wall_shadow.put.credit != null ? ` ($${live.magnet_at_wall_shadow.put.credit.toFixed(2)})` : ''}`
                : ''}
              {live.magnet_at_wall_shadow_eod ? ' · EOD replay logged for the study' : ' · replay logs at EOD'}. Research only — not a position.
            </Typography>
          )}
          {/* v0.41.0: OPG wall−2 put template on a negative morning — data
              only, never a trade; on high-IV days it is the only put-side
              information the engine produced */}
          {live?.below_wall_shadow && (() => {
            const r = live.below_wall_shadow!;
            const d = live.below_wall_shadow_eod;
            const ours = d?.rules?.our_exits;
            const his = d?.rules?.no_wall_exit;
            const fmtCt = (v: any) => (v == null ? null : `${v < 0 ? '−' : '+'}$${Math.abs(v).toFixed(0)}/ct`);
            return (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}>
                OPG template (study, not a trade): wall−2 put {r.short_strike ?? '?'}/{r.long_strike ?? '?'}
                {r.credit != null ? ` @ $${r.credit.toFixed(2)}` : ''}
                {(r.ts_et || r.first_scan) ? ` at ${String(r.ts_et || r.first_scan).slice(11, 16)}` : ''}
                {r.iv_bucket ? ` (${r.iv_bucket} IV)` : ''}
                {d && ours?.exit ? ` — our exits ${fmtCt(ours.pnl_per_ct)} (${String(ours.exit).replace(/_/g, ' ')}${ours.at ? ` ${String(ours.at).slice(11, 16)}` : ''})` : ''}
                {d && his ? `, his ${fmtCt(his.pnl_per_ct)}` : ''}
                {!d ? ' · scores at EOD' : ''}. Per contract, replayed on 1-min bars — never in the P&L.
              </Typography>
            );
          })()}
          {/* v0.40.0: what the other stop rules would have done — grey table
              under a stopped trade (leader). live_rule is calibration; the
              full study lives on the Shadows tab. */}
          {live?.exit_shadow_eod?.alternatives && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block' }}>
                What the other stop rules would have done (per trade, replayed on 1-min bars — data only, the live rule is unchanged):
              </Typography>
              {['live_rule', 'floor_width', 'high_iv_mult', 'stop_confirm', 'wall_confirm', 'double_touch', 'confirm3_backstop', 'spot_through', 'wall_only', 'hold'].map((name) => {
                const a = live.exit_shadow_eod!.alternatives![name];
                if (!a) return null;
                const pnl = a.pnl_total;
                const delta = a.delta_vs_actual;
                return (
                  <Typography key={name} variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', pl: 1.5 }}>
                    {name.replace(/_/g, ' ')}{name === 'live_rule' ? ' (calibration)' : ''}: {a.exit ? String(a.exit).replace(/_/g, ' ') : '—'}
                    {a.at ? ` @ ${String(a.at).slice(11, 16)}` : ''}
                    {pnl != null ? ` · ${pnl < 0 ? '−' : '+'}$${Math.abs(pnl).toFixed(0)}` : ''}
                    {delta != null ? ` (Δ ${delta < 0 ? '−' : '+'}$${Math.abs(delta).toFixed(0)} vs actual)` : ''}
                  </Typography>
                );
              })}
            </Box>
          )}
          {/* v0.34.0: cushion-blocked put morning — the would-be spread runs as
              a shadow replay (grey, never P&L) */}
          {live?.wall_at_spot_shadow && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}>
              Shadow (not traded): the cushion floor blocked the {live.wall_at_spot_shadow.short_strike ?? '?'}/{live.wall_at_spot_shadow.long_strike ?? '?'} put spread
              {live.wall_at_spot_shadow.credit != null ? ` ($${live.wall_at_spot_shadow.credit.toFixed(2)} credit)` : ''}
              {live.wall_at_spot_shadow.cushion_pct != null ? ` — wall ${live.wall_at_spot_shadow.cushion_pct}% from spot` : ''}
              {live.wall_at_spot_shadow_eod ? ' · EOD replay logged for the exit-rule study' : ' · replay logs at EOD'}.
            </Typography>
          )}
          {/* v0.30.0: wall-strike preference in dark mode — the layer computed
              it WOULD have sold the wall strike but isn't armed yet */}
          {live?.trade?.wall_strike_preference &&
            live.trade.wall_strike_preference.would_fire === true &&
            live.trade.wall_strike_preference.fired !== true &&
            (live.status === 'open' || live.status === 'closed') && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}>
                Wall-strike preference (dark mode): the wall strike
                {live.trade.wall_strike_preference.wall != null ? ` ${live.trade.wall_strike_preference.wall}` : ''} would have paid
                {live.trade.wall_strike_preference.wall_credit != null ? ` $${live.trade.wall_strike_preference.wall_credit.toFixed(2)}` : ' more'}
                {live.trade.wall_strike_preference.template_credit != null ? ` vs $${live.trade.wall_strike_preference.template_credit.toFixed(2)}` : ''} — not armed, did not change the trade.
              </Typography>
            )}
          {/* v0.20.0: the IV-crush detector lifted the event blackout early —
              the entry that followed is deliberately half-sized */}
          {live?.adaptive_resume &&
            (live.status === 'open' || live.status === 'closed') && (
              <Typography variant="caption" sx={{ color: '#81c784', display: 'block', mt: 0.5 }}>
                The system read the post-event IV crush and entered early at reduced size
                {live.adaptive_resume.iv_peak != null && live.adaptive_resume.iv_now != null
                  ? ` (IV ${(live.adaptive_resume.iv_peak * 100).toFixed(1)}% → ${(live.adaptive_resume.iv_now * 100).toFixed(1)}%)`
                  : ''}
                {live.event_blackout?.event ? ` — ${live.event_blackout.event} digested ahead of the fixed resume.` : '.'}
              </Typography>
            )}
        </Box>
      )}

      {loading && !gexMap && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {notFound && !gexMap && !loading && (
        <Alert
          severity={!isWeekend && minutes >= 10 * 60 ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => fetchMap(true)}>
              Load sample map
            </Button>
          }
        >
          {!isToday
            ? `No gamma map on file for ${effectiveDate}.`
            : isWeekend
              ? 'No trading session today — the gamma map is computed on trading days from ~09:30 ET.'
              : minutes >= 10 * 60
                ? `Gamma map unavailable for today (checked ${hhmm} ET). A previous day's map is never shown in its place.`
                : `Not yet computed (${hhmm} ET) — the strategy writes the first map at ~09:30 ET (warm-up scans).`}
        </Alert>
      )}

      {gexMap && chart && (
        <>
          <ResponsiveContainer width="100%" height={420}>
            <ComposedChart data={chart.data} margin={{ top: 24, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              {/* Zone bands (under the bars): profit / loss / max-loss */}
              {overlay && overlay.bands.map((b) => (
                <ReferenceArea key={b.key} x1={b.x1} x2={b.x2} fill={b.fill} fillOpacity={b.opacity} ifOverflow="hidden" />
              ))}
              <XAxis
                type="number"
                dataKey="strike"
                domain={[chart.minStrike - 1, chart.maxStrike + 1]}
                tickCount={12}
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                label={{ value: 'Strike', position: 'insideBottom', offset: -2, fontSize: 11 }}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) => `${v}`}
                label={{ value: 'GEX ($M) — calls +, puts −', angle: -90, position: 'insideLeft', fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid rgba(255,255,255,0.2)' }}
                labelStyle={{ color: '#ffffff' }}
                itemStyle={{ color: '#ffffff' }}
                formatter={(value: any) => {
                  const v = Number(value);
                  return [
                    `${v >= 0 ? '+' : ''}${v.toFixed(2)}M — ${v >= 0 ? 'dealers dampen here (calls dominate)' : 'dealers amplify here (puts dominate)'}`,
                    'GEX',
                  ];
                }}
                labelFormatter={(label: any) => `Strike ${label}`}
              />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.4)" />
              {chart.levelLines.map((l) => (
                <ReferenceLine
                  key={l.label}
                  x={l.value}
                  stroke={l.stroke}
                  strokeDasharray={l.dash}
                  strokeWidth={l.isSpot ? 2 : 1.5}
                  label={{
                    value: l.label,
                    angle: -90,
                    position: 'insideTopLeft',
                    fill: l.stroke,
                    fontSize: 10,
                    fontWeight: 'bold',
                    dy: 10,
                  }}
                />
              ))}
              {/* Short strike + structural auto-exit line (merged when they share a strike) */}
              {overlay && (overlay.combined ? (
                <ReferenceLine
                  x={overlay.S}
                  stroke="#ffb300"
                  strokeWidth={2.5}
                  label={{
                    value: `SHORT + AUTO-EXIT ${fmtStrike(overlay.S)}`,
                    angle: -90,
                    position: 'insideBottomLeft',
                    fill: '#ffb300',
                    fontSize: 10,
                    fontWeight: 'bold',
                    dy: -10,
                  }}
                />
              ) : (
                <>
                  <ReferenceLine
                    x={overlay.S}
                    stroke="#29b6f6"
                    strokeDasharray="8 3"
                    strokeWidth={1.5}
                    label={{
                      value: `SHORT ${fmtStrike(overlay.S)}`,
                      angle: -90,
                      position: 'insideBottomLeft',
                      fill: '#29b6f6',
                      fontSize: 10,
                      fontWeight: 'bold',
                      dy: -10,
                    }}
                  />
                  <ReferenceLine
                    x={overlay.E}
                    stroke="#ffb300"
                    strokeWidth={2.5}
                    label={{
                      value: `AUTO-EXIT ${fmtStrike(overlay.E)}`,
                      angle: -90,
                      position: 'insideBottomLeft',
                      fill: '#ffb300',
                      fontSize: 10,
                      fontWeight: 'bold',
                      dy: -10,
                    }}
                  />
                </>
              ))}
              <Bar dataKey="gexM" barSize={11}>
                {chart.data.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry.gexM >= 0 ? '#4caf50' : '#f44336'}
                    fillOpacity={Math.abs(entry.gexM) < 0.05 * chart.maxAbs ? 0.3 : 1}
                  />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>

          {/* Zone legend (only when a trade is overlaid) */}
          {overlay && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5, mb: 0.5 }}>
              {[
                { c: '#4caf50', t: 'Profit zone' },
                { c: '#f44336', t: 'Loss zone' },
                { c: '#b71c1c', t: `Max loss${overlay.maxLoss != null ? ` (${fmtSignedMoney0(-Math.abs(overlay.maxLoss))})` : ''}` },
                { c: '#ffb300', t: 'Auto-exit level' },
              ].map((item) => (
                <Box key={item.t} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: item.c, borderRadius: 0.5, opacity: 0.8 }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{item.t}</Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
              • Big green bars ABOVE spot = overhead dealer supply; rallies stall into them (call wall).
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
              • Red bars BELOW spot = below the put wall selling accelerates; the strategy sells its spread BELOW that zone, not inside it.
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
              • Spot above the flip point with net GEX &gt; 0 = positive regime = pinned, mean-reverting day = the strategy trades. Anything else = it stands aside.
            </Typography>
            <Link
              component="button"
              variant="caption"
              onClick={() => setShowHelp((s) => !s)}
              sx={{ mt: 0.5, color: '#90caf9' }}
            >
              {showHelp ? 'Hide' : 'How to read this'}
            </Link>
            <Collapse in={showHelp}>
              <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid rgba(255,255,255,0.15)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
                  • Each bar is dealer gamma exposure at that strike. Positive (green) = dealers dampen moves there;
                  negative (red) = dealers amplify. Faded bars are small enough to ignore.
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
                  • The shaded bands show where THIS trade wins (green), loses (red), and hits its capped max loss (deep red).
                  The amber line is the structural auto-exit: the system closes the trade if SPY crosses it.
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
                  • A put credit spread wins if SPY closes ABOVE the short strike; a call credit spread wins if it closes BELOW.
                  Everything settles same-day — nothing is held overnight.
                </Typography>
              </Box>
            </Collapse>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default GexMapView;
